import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InventoryItems = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: '',
    color: '',
    quantity: 0,
    min_quantity: 0
  });

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/inventory`, { headers: getAuthHeader() });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory`, formData, { headers: getAuthHeader() });
      toast.success('Item added successfully');
      setIsOpen(false);
      fetchItems();
      setFormData({
        name: '',
        category: '',
        unit: '',
        color: '',
        quantity: 0,
        min_quantity: 0
      });
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDelete = async (name) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    try {
      await axios.delete(`${API}/inventory/${name}`, { headers: getAuthHeader() });
      toast.success('Item deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('inventoryItems')}</h2>
        {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-inventory-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>Add Inventory Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="inventory-name-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Category</Label>
                <Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required data-testid="inventory-category-input" placeholder="e.g. Fabric, Zipper, Thread" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Color</Label>
                <Input value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} data-testid="inventory-color-input" placeholder="e.g. Red, Blue, Black" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Unit</Label>
                <Input value={formData.unit} onChange={(e) => setFormData({...formData, unit: e.target.value})} required data-testid="inventory-unit-input" placeholder="e.g. meters, pieces, kg" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Quantity</Label>
                <Input type="number" step="0.01" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value)})} required data-testid="inventory-quantity-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Minimum Quantity (Alert Level)</Label>
                <Input type="number" step="0.01" value={formData.min_quantity} onChange={(e) => setFormData({...formData, min_quantity: parseFloat(e.target.value)})} data-testid="inventory-min-input" />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="inventory-submit-btn">
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Category</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Color</th>
              <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Unit</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Quantity</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Min. Qty</th>
              <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('status')}</th>
              {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const isLow = item.quantity <= item.min_quantity;
              return (
                <tr key={idx} className={`hover:bg-muted/30 ${isLow ? 'bg-warning/5' : ''}`} data-testid={`inventory-row-${idx}`}>
                  <td className="border border-border p-3">{item.name}</td>
                  <td className="border border-border p-3">{item.category}</td>
                  <td className="border border-border p-3">{item.color || '-'}</td>
                  <td className="border border-border p-3 text-center">{item.unit}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric">{item.quantity.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric">{item.min_quantity.toFixed(2)}</td>
                  <td className="border border-border p-3 text-center">
                    {isLow ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/20 text-warning rounded text-xs" data-testid={`low-stock-${idx}`}>
                        <AlertTriangle className="w-3 h-3" />
                        Low Stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 bg-success/20 text-success rounded text-xs">OK</span>
                    )}
                  </td>
                  {user?.role === 'admin' && (
                    <td className="border border-border p-3 text-center">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.name)} data-testid={`delete-inventory-${idx}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-inventory">
            <p lang={language === 'ar' ? 'ar' : 'en'}>No inventory items yet</p>
          </div>
        )}
      </div>
    </Card>
  );
};